import { SecurityEventsTabPanel } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityTabs/SecurityEventsTabPanel.tsx';
import { SecurityOverviewTabPanel } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/SecurityTabs/SecurityOverviewTabPanel/SecurityOverviewTabPanel.tsx';
import { SecurityDetailsControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Securities/SecurityDetails/useSecurityDetailsController.ts';
import { AuditView } from '@dealstack/frontend/shared/components/Audit/Audit.tsx';
import {
	AuditContext,
	useAuditController,
} from '@dealstack/frontend/shared/components/Audit/useAuditController.ts';
import { MantineTabs } from '@dealstack/frontend/shared/components/MantineTabs/MantineTabs.tsx';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { MantineTabProps } from '@dealstack/frontend/shared/components/MantineTabs/MantineTab.types';
import { SecurityAllocationsTabPanel } from './SecurityAllocationsTabPanel/SecurityAllocationsTabPanel.tsx';
import { SecurityMoreDetailsTabPanel } from './SecurityMoreDetailsTabPanel';

enum SECURITY_TABS {
	OVERVIEW = 'OVERVIEW',
	SECURITY_EVENTS = 'SECURITY_EVENTS',
	SECURITY_ALLOCATIONS = 'SECURITY_ALLOCATIONS',
	MORE_DETAILS = 'MORE_DETAILS',
	HISTORY = 'HISTORY',
}

interface Props {
	hasEditorPermission?: boolean;
}

export const SecurityTabs = ({ hasEditorPermission }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { securityId } = useParams();
	const auditController = useAuditController(securityId);

	const { getSecurityEventRequest, getAllocationsRequest } = useContext(
		SecurityDetailsControllerContext
	);

	const eventsCount = getSecurityEventRequest.data?.totalCount ?? 0;
	const allocationsCount = getAllocationsRequest.data?.length ?? 0;

	const tabsConfig = useMemo(
		(): MantineTabProps<SECURITY_TABS>[] => [
			{
				label: tCapManage('securities.details.overview'),
				value: SECURITY_TABS.OVERVIEW,
			},
			{
				label: tCapManage('securities.details.events'),
				value: SECURITY_TABS.SECURITY_EVENTS,
				count: eventsCount,
				testId: TEST_ELEMENT_IDS.CAP_TAB_EVENTS,
			},
			{
				label: tCapManage('securities.details.securityAllocations'),
				value: SECURITY_TABS.SECURITY_ALLOCATIONS,
				count: allocationsCount,
			},
			{
				label: tCapManage('securities.details.moreDetails'),
				value: SECURITY_TABS.MORE_DETAILS,
			},
			{
				label: tCapManage('securities.details.history', { defaultValue: 'History' }),
				value: SECURITY_TABS.HISTORY,
			},
		],
		[tCapManage, eventsCount, allocationsCount]
	);

	const panelsConfig = useMemo(
		() => [
			{
				value: SECURITY_TABS.OVERVIEW,
				content: <SecurityOverviewTabPanel hasEditorPermission={hasEditorPermission} />,
			},
			{
				value: SECURITY_TABS.SECURITY_EVENTS,
				content: <SecurityEventsTabPanel hasEditorPermission={hasEditorPermission} />,
			},
			{
				value: SECURITY_TABS.SECURITY_ALLOCATIONS,
				content: <SecurityAllocationsTabPanel hasEditorPermission={hasEditorPermission} />,
			},
			{
				value: SECURITY_TABS.MORE_DETAILS,
				content: <SecurityMoreDetailsTabPanel />,
			},
			{
				value: SECURITY_TABS.HISTORY,
				content: <AuditView />,
			},
		],
		[hasEditorPermission]
	);

	return (
		<AuditContext.Provider value={auditController}>
			<MantineTabs
				defaultValue={SECURITY_TABS.OVERVIEW}
				tabs={tabsConfig}
				panels={panelsConfig}
			/>
		</AuditContext.Provider>
	);
};
